import { useEffect, useState } from "react";
import { ReactFlow, Background, Controls, BezierEdge } from "@xyflow/react";
import ModalCustomized from "../Modal/Modal";
import TextField from "../TextField";
import { Button } from "reactstrap";
import { showMessage } from "../../helpers/message";

function OffsetBezierEdge({ id, sourceX, sourceY, targetX, targetY, style, markerEnd, label, data }) {
    const offset = data?.offset || 0;
    const horizontalOffset = data?.horizontalOffset || 0;

    const controlOffsetX = 200 + Math.abs(offset) + horizontalOffset;
    const controlOffsetY = -offset;

    const path = `M ${sourceX} ${sourceY} C 
      ${sourceX + controlOffsetX} ${sourceY + controlOffsetY}, 
      ${targetX - controlOffsetX} ${targetY + controlOffsetY}, 
      ${targetX} ${targetY}`;

    const labelX = (sourceX + targetX) / 2;
    const labelY = (sourceY + targetY) / 2 + controlOffsetY - 15;

    return (
        <>
            <path
                id={id}
                style={{ ...style }}
                className="react-flow__edge-path"
                d={path}
                markerEnd={markerEnd}
            />
            {label && (
                <text
                    x={labelX}
                    y={labelY}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    style={{ fontSize: 12, fill: "#333", fontWeight: 500 }}
                >
                    {label}
                </text>
            )}
        </>
    );
}

export default function ReactFlowComponent({ nodes = [], onClickAdicionar, onClickConector, editavel }) {
    const [flowNodes, setFlowNodes] = useState([]);
    const [flowEdges, setFlowEdges] = useState([]);

    const [listFlowEdgesOptions, setListFlowEdgesOptions] = useState([]);
    const [selectOptionAcao, setSelectOptionAcao] = useState("");

    const [openModal, setOpenModal] = useState(false);
    const toggleModal = () => setOpenModal(!openModal);

    useEffect(() => {
        if (!nodes?.length) {
            setFlowNodes([]);
            setFlowEdges([]);
            return;
        }

        // ---------- Calcular linhas ----------
        const resultado = [];
        const fila = [];

        const raiz = nodes.filter(n => !n.hashPai).map(n => ({ ...n, linha: 1 }));
        resultado.push(...raiz);
        fila.push(...raiz);

        while (fila.length) {
            const atual = fila.shift();
            const filhos = nodes
                .filter(n => n.hashPai === atual.hash && !resultado.some(r => r.hash === n.hash && r.hashPai === n.hashPai))
                .map(n => ({ ...n, linha: atual.linha + 1 }));
            resultado.push(...filhos);
            fila.push(...filhos);
        }

        // ---------- Adicionar posicaoLinha ----------
        const nodesComPosicao = resultado.map(n => {
            const irmãos = resultado.filter(x => x.linha === n.linha);
            const index = irmãos.findIndex(i => i.hash === n.hash);
            return { ...n, posicaoLinha: index + 1 };
        });

        const espacoHorizontal = 400;
        const espacoVertical = 280;

        const nodeMap = new Map();
        const rfNodes = [];

        nodesComPosicao.forEach(n => {
            if (!nodeMap.has(n.hash)) {
                const nodeId = `${n.hash}-${n.linha}-${n.posicaoLinha}`;
                nodeMap.set(n.hash, nodeId);

                rfNodes.push({
                    id: nodeId,
                    position: {
                        x: (n.posicaoLinha * espacoHorizontal) - (espacoHorizontal / 2),
                        y: (n.linha - 1) * espacoVertical
                    },
                    data: {
                        label: (
                            <div style={{ position: "relative" }}>
                                {n.name}
                                {editavel && onClickAdicionar && !n.etapaFinal && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            right: '-30px',
                                            top: '-25px',
                                            width: '35px',
                                            borderRadius: '50%',
                                            background: '#4CAF50',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'white',
                                            fontSize: '24px',
                                            cursor: 'pointer',
                                            boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                                            zIndex: 10
                                        }}
                                        onClick={() => onClickAdicionar(n.hash)}
                                    >
                                        +
                                    </div>
                                )}
                            </div>
                        )
                    },
                    style: {
                        padding: 10,
                        borderRadius: 6,
                        border: `2px solid #999`,
                        background: n.etapaInicial ? "#c8e6c9" :
                            n.etapaFinal ? "#ffcdd2" : "#fff",
                        width: 180,
                        opacity: 1
                    }
                });
            }
        });

        // ---------- Criar edges ----------
        const edgeCountMap = new Map();
        const rfEdges = [];

        nodesComPosicao
            .filter(n => n.hashPai)
            .forEach((n, index) => {
                const parentId = nodeMap.get(n.hashPai);
                const targetId = nodeMap.get(n.hash);

                const key = `${parentId}-${targetId}`;
                const count = edgeCountMap.get(key) || 0;
                edgeCountMap.set(key, count + 1);

                const horizontalOffset = (count - (edgeCountMap.get(key) - 1) / 2) * 2;
                const verticalOffset = (count - (edgeCountMap.get(key) - 1) / 2) * 1.5;

                const offset = (count - (edgeCountMap.get(key) - 1) / 2) * 60;

                rfEdges.push({
                    id: `edge-${n.hashPai}-${n.hash}-${index}`,
                    source: parentId,
                    target: targetId,
                    label: n.textoConector,
                    type: "offsetBezier",
                    animated: true,
                    style: {
                        stroke: "#555",
                        cursor: "pointer",
                        strokeDasharray: "none"
                    },
                    markerEnd: { type: "arrowclosed" },
                    data: {
                        hash: n.hash,
                        hashPai: n.hashPai,
                        acaoId: n.acaoId,
                        offset: offset + verticalOffset,
                        horizontalOffset,
                        tipo: n.tipo
                    }
                });
            });

        setFlowNodes(rfNodes);
        setFlowEdges(rfEdges);
    }, [nodes, editavel, onClickAdicionar]);

    const onEdgeClick = (event, edge) => {
        event.stopPropagation();

        // todas as edges entre os mesmos nós
        const mesmasEdges = flowEdges.filter(
            e => e.source === edge.source && e.target === edge.target
        );

        if (mesmasEdges.length > 1) {
            const options = mesmasEdges.map((e, i) => ({
                value: e.data.acaoId,
                label: e.label
            }));

            let _options = [{ value: "0", label: "Selecione" }, ...options]

            setListFlowEdgesOptions(_options);
            setSelectOptionAcao(_options[0].value);
            setOpenModal(true);
        } else {
            setOpenModal(false);
            setListFlowEdgesOptions([]);
            onClickConector(edge.data.hash, edge.data.hashPai, edge.data.acaoId);
        }
    };

    return (
        <>
            <ModalCustomized
                size={'md'}
                fullscreen={"md"}
                toggle={toggleModal}
                widthPadrao={false}
                isOpen={openModal}
                btnClose={() => {
                    setSelectOptionAcao("");
                    toggleModal();
                }}
                title={"Selecione a ação desejada!"}
            >
                <TextField
                    name={"acao"}
                    label={"Ação "}
                    type={"select"}
                    options={listFlowEdgesOptions}
                    onChange={(e) => {
                        const { value } = e.target;
                        setSelectOptionAcao(value)
                    }}
                    value={selectOptionAcao}
                />
                <div className="d-flex justify-content-center align-items-center">
                    <Button color="success" onClick={() => {
                        if (!!selectOptionAcao && selectOptionAcao !== "0") {
                            let [acao] = flowEdges.filter(x => x.data.acaoId === Number(selectOptionAcao));
                            toggleModal();
                            setSelectOptionAcao("")
                            onClickConector(acao.data.hash, acao.data.hashPai, acao.data.acaoId);
                        } else showMessage("Alerta", "Escolha uma ação para abrir o modal com a ação selecionada!", "warning", null);

                    }}>
                        Confirmar escolha
                    </Button>
                </div>
            </ModalCustomized>
            <ReactFlow
                nodes={flowNodes}
                edges={flowEdges}
                edgeTypes={{ offsetBezier: OffsetBezierEdge }}
                onEdgeClick={onEdgeClick}
                minZoom={0.1}
                maxZoom={3}
                defaultViewport={{ x: 0, y: 0, zoom: 1 }}
                proOptions={{ hideAttribution: true }}
            >
                <Background />
                <Controls />
            </ReactFlow>
        </>
    );
}